import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Sparkles } from "lucide-react";
import { useSite, waLink, resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export const UpcomingBanners = () => {
  const settings = useSite();
  const { lang, s } = useLang();
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/banners`).then((res) => setBanners(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, []);

  if (!banners.length) return null;

  const ctaHref = (b) => {
    if (b.cta_url) return b.cta_url.startsWith("http") || b.cta_url.startsWith("/") ? b.cta_url : `https://${b.cta_url}`;
    return waLink(settings, s.bannerWaMsg(pick(lang, b, "title")), lang);
  };

  return (
    <section id="upcoming" data-testid="upcoming-section" className="py-24 md:py-32 bg-luxury-dark border-y border-luxury-border relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 right-1/4 w-[500px] h-[500px] rounded-full bg-luxury-gold/5 blur-[120px]" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="flex items-center gap-4 mb-14"
        >
          <Sparkles className="w-6 h-6 text-luxury-gold" strokeWidth={1.25} />
          <h2 className="font-serif font-bold text-4xl sm:text-5xl text-luxury-bone">
            {s.banners_title_a} <span className="gold-gradient-text italic">{s.banners_title_b}</span>
          </h2>
        </motion.div>

        <div className="space-y-8">
          {banners.map((b, i) => (
            <motion.article
              key={b.id}
              data-testid={`banner-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.12, duration: 0.8, ease }}
              className="group relative overflow-hidden rounded-3xl border border-luxury-border hover:border-luxury-gold transition-colors duration-500"
            >
              {b.image ? (
                <div className="absolute inset-0">
                  <img src={resolveMedia(b.image)} alt="" className="w-full h-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-l from-luxury-black via-luxury-black/80 to-luxury-black/40" />
                </div>
              ) : null}
              <div className="relative p-10 md:p-14 flex flex-wrap items-center justify-between gap-8">
                <div className="max-w-xl">
                  {pick(lang, b, "date_text") ? (
                    <span className="inline-flex items-center gap-2 text-xs tracking-wider text-luxury-gold border border-luxury-gold/40 rounded-full px-4 py-1.5 mb-5 bg-luxury-black/50">
                      <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {pick(lang, b, "date_text")}
                    </span>
                  ) : null}
                  <h3 className="font-serif text-3xl md:text-5xl text-luxury-bone leading-tight mb-3">{pick(lang, b, "title")}</h3>
                  {pick(lang, b, "subtitle") ? <p className="text-luxury-gray text-base md:text-lg">{pick(lang, b, "subtitle")}</p> : null}
                </div>
                <a
                  data-testid={`banner-cta-${i}`}
                  href={ctaHref(b)}
                  target={b.cta_url && b.cta_url.startsWith("/") ? "_self" : "_blank"}
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-8 py-4 rounded-full hover:bg-luxury-goldLight transition-colors duration-300 shadow-[0_10px_40px_rgba(212,175,55,0.25)]"
                >
                  {pick(lang, b, "cta_text") || s.cta_start}
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
